import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useNavigate } from 'react-router-dom';
import { bookEvent } from '../../services/apiService';
import bootstrap from 'bootstrap/dist/js/bootstrap.bundle.min.js';
import AuthCheck from '../../components/authCheck/AuthCheck';

// Load Stripe publishable key from environment variables
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

if (!stripePublishableKey) {
  console.error('REACT_APP_STRIPE_PUBLISHABLE_KEY is not defined in environment variables');
}

const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

const CheckoutForm = () => {
  const navigate = useNavigate();
  const { eventName } = useParams()

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [confirmedBooking, setConfirmedBooking] = useState(null);

  // Load booking data from sessionStorage
  useEffect(() => {
    const stored = sessionStorage.getItem('confirmedBooking');
    if (!stored) {
      navigate('/');
      return;
    }

    try {
      const parsed = JSON.parse(stored);
      setConfirmedBooking(parsed);
    } catch (err) {
      console.error('Invalid booking data');
      navigate('/');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!confirmedBooking) return;

    setProcessing(true);
    setError('');

    try {
      const { event, selectedSeats, grandTotal, platformFee } = confirmedBooking;
        console.log(confirmedBooking.eventId, 'eventId');
      const bookingData = {
        eventId: confirmedBooking.eventId,
        seats: selectedSeats.map(s => `${s.row}-${s.number}`),
        totalPrice: grandTotal,
        paymentStatus: 'pending', // Will be updated after Stripe confirmation
        platformFeeAmount: platformFee || 0,
        paymentDetails: {
          successUrl: `${window.location.origin}/payment-success`,
          cancelUrl: `${window.location.origin}/payment-cancel`,
        }
      };

      const res = await bookEvent(bookingData);

      if (res?.data?.checkoutUrl) {
        window.location.href = res.data.checkoutUrl; // Redirect to Stripe Checkout
      } else {
        throw new Error('Stripe Checkout URL not returned');
      }
    } catch (err) {
      console.error(err);
      setError('Failed to initiate payment. Please try again.');
      setProcessing(false);
    }
  };

    const handleBookingSuccess = () => {
  // Programmatically hide the modal using Bootstrap's Modal API
  const modalEl = document.getElementById('successModal');
  if (modalEl) {
    const bsModal = bootstrap.Modal.getInstance(modalEl);
    if (bsModal) {
      bsModal.hide();
    }
  }

  // Delay navigation slightly to ensure modal is closed
  setTimeout(() => {
    navigate('/my-bookings');
  }, 300); // 300ms matches Bootstrap's modal fade duration
};

  return (
    <>
    <div className="container mt-4">
          <h2>Complete Payment</h2>

          <form onSubmit={handleSubmit}>
              <p>
                  You will be redirected to a secure Stripe checkout page to complete your payment.
              </p>
              {error && <div className="text-danger mb-2">{error}</div>}
              <button type="submit" className="btn btn-primary" disabled={processing}>
                  {processing ? 'Processing...' : 'Proceed to Payment'}
              </button>
          </form>
      </div>
      <div
          className="modal fade"
          id="successModal"
          tabIndex="-1"
          aria-labelledby="successModalLabel"
          aria-hidden="true"
      >
              <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                      <div className="modal-header">
                          <h5 className="modal-title" id="successModalLabel">Booking Confirmed</h5>
                          <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                      </div>
                      <div className="modal-body">
                          🎉 Your booking has been successfully confirmed!
                      </div>
                      <div className="modal-footer">
                          <button
                              type="button"
                              className="btn btn-secondary"
                              data-bs-dismiss="modal"
                          >
                              Close
                          </button>
                          <button
                              type="button"
                              className="btn btn-primary"
                              onClick={handleBookingSuccess}
                          >
                              View My Bookings
                          </button>
                      </div>
                  </div>
              </div>
          </div>
          </>
  );
};

const PaymentPage = () => {
  if (!stripePromise) {
    return (
      <AuthCheck redirectTo="/">
        <div className="container mt-4">
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">Payment Configuration Error</h4>
            <p>
              Stripe payment is not properly configured. Please ensure that <code>REACT_APP_STRIPE_PUBLISHABLE_KEY</code> is set in your environment variables.
            </p>
            <hr />
            <p className="mb-0">
              Contact the administrator or check your <code>.env</code> file.
            </p>
          </div>
        </div>
      </AuthCheck>
    );
  }

  return (
    <AuthCheck redirectTo="/">
      <Elements stripe={stripePromise}>
        <CheckoutForm />
      </Elements>
    </AuthCheck>
  );
};

export default PaymentPage;
